import { useState, useEffect, memo, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft, Sparkles, 
  Play, Award, Briefcase, TrendingUp, Users, CheckCircle,
  Instagram, Linkedin, Globe, ChevronRight, X, ZoomIn
} from 'lucide-react'
import Reveal from '../components/landing/Reveal'

// Student Portfolio Work
const studentWork = [
  {
    id: 1,
    title: 'Kampala City Scape',
    student: 'SMA Student',
    category: 'Photography',
    image: '/Gallery/Academy/showcase/Kampala City Scape.jpg',
    tools: 'Canon EOS R5 + Lightroom',
    description: 'Professional editorial photography showcasing Kampala cityscape'
  },
  {
    id: 2,
    title: 'Quickcredit App',
    student: 'SMA Team',
    category: 'Graphic Design',
    image: '/Gallery/Academy/screenshots/QC.png',
    tools: 'Figma + React',
    description: 'Admin portal design for Quickcredit financial services platform',
    link: 'https://quickcredit-adminportal.netlify.app/login'
  },
  {
    id: 3,
    title: 'Cyberpunk Kampala',
    student: 'Grace K.',
    category: 'AI Art',
    image: '/Gallery/Academy/showcase/cyberpunk.jpg',
    tools: 'Midjourney + Photoshop',
    description: 'AI-generated concept art blending traditional and futuristic themes',
    isAI: true
  },
  {
    id: 4,
    title: 'Kampala Coffee Branding',
    student: 'John B.',
    category: 'Graphic Design',
    image: '/Gallery/ai-generated/Logo Design for Local Business.png',
    tools: 'Adobe Illustrator + AI',
    description: 'Complete brand identity for local coffee shop',
    hasBeforeAfter: true,
    beforeImage: '/Gallery/124414.jpg',
    afterImage: '/Gallery/ai-generated/Logo Design for Local Business.png'
  },
  {
    id: 5,
    title: 'Academy Live Session',
    student: 'Patricia L.',
    category: 'Video',
    youtubeId: '35lUT3wQ_cE',
    tools: 'Sony A7III + Premiere Pro',
    description: 'Behind-the-scenes look at a live academy training session in action'
  },
  {
    id: 6,
    title: 'Judith — Portrait Series',
    student: 'Judith',
    category: 'Photography',
    image: '/Gallery/Academy/potrait series/Judith 2 copy.jpg',
    tools: 'Nikon Z6 + Capture One',
    description: 'Professional studio portrait featuring model Judith'
  }
]

// Campus Life Stories
const campusStories = [
  {
    image: '/Gallery/Academy/showcase/Kampala City Scape.jpg',
    week: 'Week 1',
    caption: 'Professional editorial photography showcasing Kampala cityscape',
    category: 'Photography'
  },
  {
    image: '/Gallery/Academy/showcase/thinking.jpg',
    week: 'Week 2',
    caption: 'Design thinking workshop - From concept to execution',
    category: 'Graphic Design'
  },
  {
    image: '/Gallery/DSC02406 copy.jpg',
    week: 'Week 5',
    caption: 'Advanced color grading workshop with professional colorist',
    category: 'Video Production'
  },
  {
    image: '/Gallery/Academy/showcase/AI.jpg',
    week: 'Week 7',
    caption: 'AI tools integration: Combining traditional and modern workflows',
    category: 'All Programs'
  },
  {
    image: '/Gallery/Academy/Class rooms/20250822_165220.jpg',
    week: 'Workshop 1',
    caption: 'ON SET: Behind the scenes of a real production shoot',
    category: 'Multimedia'
  },
  {
    image: '/Gallery/Academy/Workshops/1X4A5722 copy.jpg',
    week: 'Workshop 2',
    caption: 'Hands-on training in studio lighting and camera operation',
    category: 'Photography'
  },
  {
    image: '/Gallery/Academy/showcase/workshop.jpg',
    week: 'Workshop 3',
    caption: 'Group critique session: Refining work with peer feedback',
    category: 'All Programs'
  },
  {
    image: '/Gallery/Academy/Workshops/ww.jpg',
    week: 'Workshop 4',
    caption: 'Professional video editing workflow with Premiere Pro and DaVinci Resolve',
    category: 'Video Production'
  }
]

// Alumni Success Stories
const alumniSpotlight = [
  {
    name: 'Mark Tumusiime',
    role: 'Lead Designer',
    company: 'SafeBoda',
    image: '/Tutors/placeholder.jpg',
    studentWork: '/Gallery/ai-generated/Logo Design for Local Business.png',
    quote: 'From beginner to professional designer in 12 weeks. SMA gave me the skills and confidence to land my dream job.',
    linkedin: '#',
    portfolio: '#'
  },
  {
    name: 'Annet Nakato',
    role: 'Freelance Photographer',
    company: 'Self-Employed',
    image: '/Tutors/placeholder.jpg',
    studentWork: '/Gallery/268A5755.JPG',
    quote: 'Now shooting for international NGOs and earning 3x my previous salary. The AI photography tools they taught are game-changers.',
    instagram: '#',
    portfolio: '#'
  },
  {
    name: 'Brian Ssemanda',
    role: 'Video Producer',
    company: 'NTV Uganda',
    image: '/Tutors/placeholder.jpg',
    studentWork: '/Gallery/DSC02420 copy.jpg',
    quote: 'The hands-on training and small class size made all the difference. I got hired before I even graduated.',
    linkedin: '#'
  }
]

const categories = ['All', 'Photography', 'Graphic Design', 'Video', 'AI Art']

// Memoized Portfolio Row Component - Horizontal Open-Edge Design
const PortfolioRow = memo(({ work, index, setLightboxImage, setSelectedVideo }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  
  // Parse tools into array
  const toolsArray = work.tools.split('+').map(t => t.trim()).filter(Boolean)
  
  return (
    <div className="group py-12 relative">
      {/* Top divider */}
      {index === 0 && <div className="absolute top-0 left-0 right-0 h-px bg-white/10" />}
      
      {/* 2-Column Horizontal Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Column 1: Info */}
        <div className="order-2 lg:order-1 space-y-6">
          <div>
            <h3 className="cinema-headline text-2xl sm:text-3xl mb-2">
              {work.title}
            </h3>
            <p className="text-cinema-gold text-xs uppercase tracking-[0.25em] mb-4 font-sans">
              by {work.student} · {work.category}
            </p>
            <p className="cinema-body text-base">
              {work.description}
            </p>
          </div>
          
          {/* Tools Used - Ghost Pills */}
          <div>
            <p className="text-xs text-cinema-muted uppercase tracking-[0.2em] mb-3 font-sans">
              Tools Used
            </p>
            <div className="flex flex-wrap gap-2">
              {toolsArray.map((tool, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 text-cinema-soft text-xs font-sans group-hover:text-cinema-gold transition-colors duration-300"
                >
                  {tool}
                </span>
              ))}
              {work.isAI && (
                <span className="px-3 py-1.5 text-cinema-gold text-xs flex items-center gap-1 font-sans font-medium">
                  <Sparkles className="w-3 h-3" />
                  AI Enhanced
                </span>
              )}
            </div>
          </div>

          {work.link && (
            <a
              href={work.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-cinema-gold hover:text-white text-xs uppercase tracking-[0.2em] font-sans font-medium transition-colors group/link"
            >
              <Globe className="w-4 h-4" />
              View Live Project
              <ArrowLeft className="w-3 h-3 rotate-[135deg] group-hover/link:translate-x-0.5 transition-transform" />
            </a>
          )}
        </div>

        {/* Column 2: The Work */}
        <div className="order-1 lg:order-2 relative group/image">
          <div className="relative aspect-[4/3] overflow-hidden bg-cinema-graphite">
            {work.youtubeId ? (
              <>
                <img 
                  src={`https://img.youtube.com/vi/${work.youtubeId}/maxresdefault.jpg`}
                  alt={`${work.title} - Student video project at Synergy Multimedia Academy Kampala`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover/image:scale-105"
                  loading="lazy"
                  decoding="async"
                  onLoad={() => setImageLoaded(true)}
                />
                <button
                  onClick={() => setSelectedVideo(work)}
                  className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover/image:bg-black/40 transition-colors"
                >
                  <div className="bg-cinema-glow p-5 transform group-hover/image:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white fill-white" />
                  </div>
                </button>
              </>
            ) : (
              <>
                <img 
                  src={work.image}
                  alt={work.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover/image:scale-105"
                  loading="lazy"
                  onLoad={() => setImageLoaded(true)}
                />
                {/* Click to expand overlay */}
                <button
                  onClick={() => setLightboxImage(work.image)}
                  className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover/image:opacity-100"
                >
                  <div className="p-3 transform scale-75 group-hover/image:scale-100 transition-transform">
                    <ZoomIn className="w-6 h-6 text-cinema-gold" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />
    </div>
  )
})

PortfolioRow.displayName = 'PortfolioRow'

export default function ShowcasePage() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [lightboxImage, setLightboxImage] = useState(null)

  // Memoize filtered work to avoid recalculation on every render
  const filteredWork = useMemo(() => {
    return selectedCategory === 'All' 
      ? studentWork 
      : studentWork.filter(work => work.category === selectedCategory)
  }, [selectedCategory])

  useEffect(() => {
    document.title = 'Graduate Showcase | Photography School Uganda Success Stories | Multimedia Academy Kampala'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) metaDesc.setAttribute('content', 'Success stories from Synergy Multimedia Academy graduates in Kampala. See Photography School alumni, Videography & AI Creative Courses graduates now working across Uganda.')
  }, [])

  // Keyboard support for modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxImage(null)
        setSelectedVideo(null)
      }
    }
    
    if (lightboxImage || selectedVideo) {
      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }
  }, [lightboxImage, selectedVideo])

  // Memoize callbacks
  const handleVideoSelect = useCallback((work) => {
    setSelectedVideo(work)
  }, [])

  const handleLightboxOpen = useCallback((image) => {
    setLightboxImage(image)
  }, [])

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category)
  }, [])

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <div className="cinema-grain" aria-hidden="true" />
      {/* Header */}
      <nav className="cinema-glass-strong sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <Link to="/explore" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-3 group">
              <img
                src="/Logos/SMA LOGO  DARK MODE.png"
                alt="Synergy Multimedia Academy logo"
                className="h-10 sm:h-12 transition-transform duration-500 group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <p className="font-display text-sm tracking-tight leading-tight text-white">Synergy Multimedia</p>
                <p className="text-xs uppercase tracking-[0.25em] text-cinema-gold font-medium">Academy</p>
              </div>
            </Link>
            <div className="flex items-center gap-6 sm:gap-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-cinema-soft hover:text-cinema-gold text-xs uppercase tracking-[0.2em] font-sans font-medium transition-colors group"
              >
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <Link to="/register" className="cinema-btn-primary !px-6 !py-3 !min-h-[44px] !text-xs">
                Enroll
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-[url('/Gallery/Academy/Workshops/ww.jpg')] bg-cover bg-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cinema-black/70 via-cinema-black/50 to-cinema-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-cinema-black/80 via-transparent to-cinema-black/40" />
        <div className="cinema-glow-orb w-[600px] h-[600px] -top-40 -right-40 opacity-60" />
        <div className="cinema-glow-orb cinema-glow-orb-blue w-[400px] h-[400px] bottom-10 -left-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col justify-center py-28 sm:py-36">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-8 mx-auto">
              <Sparkles className="w-4 h-4 text-cinema-gold" />
              <span className="cinema-label">Creative Showcase</span>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="cinema-headline-lg max-w-4xl mx-auto">
              Graduate Success Stories — <span className="text-transparent bg-clip-text bg-gradient-to-r from-cinema-gold via-cinema-glow to-cinema-amber">Photography School Uganda</span> & Multimedia Academy Kampala
            </h1>
          </Reveal>
          <Reveal delay={300}>
            <p className="mt-8 cinema-body-lg max-w-2xl mx-auto">
              Real student work. Real transformations. Real careers launched.
            </p>
          </Reveal>
          <div className="mt-16 cinema-scroll-cue flex justify-center">
            <ChevronRight className="w-7 h-7 text-cinema-gold/60 rotate-90" />
          </div>
        </div>
      </div>

      {/* SECTION 1: THE WORK (Student Portfolios) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <Reveal className="text-center mb-16">
          <span className="cinema-label">The Work</span>
          <h2 className="cinema-headline-lg mt-6 mb-4">
            Student <span className="text-cinema-gold">Portfolios</span>
          </h2>
          <p className="cinema-body-lg max-w-2xl mx-auto">
            These are actual projects created by our students. Filter by category to explore their work.
          </p>
        </Reveal>

        {/* Filter Navigation - Minimalist with Gold Underline */}
        <div className="flex flex-wrap justify-center gap-8 mb-20">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`relative pb-2 text-xs uppercase tracking-[0.2em] font-sans font-medium transition-colors ${
                selectedCategory === category ? 'text-cinema-gold' : 'text-cinema-muted hover:text-white'
              }`}
            >
              {category}
              {/* Active underline */}
              <div
                className={`absolute bottom-0 left-0 right-0 h-0.5 bg-cinema-gold transition-all duration-300 ${
                  selectedCategory === category ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Portfolio Rows - Open-Edge Horizontal Layout */}
        <div className="space-y-0">
          {filteredWork.map((work, index) => (
            <PortfolioRow
              key={work.id}
              work={work}
              index={index}
              setLightboxImage={handleLightboxOpen}
              setSelectedVideo={handleVideoSelect}
            />
          ))}
        </div>
      </section>

      {/* SECTION 2: THE PROCESS (Campus Life) */}
      <section className="bg-cinema-charcoal border-y border-white/5 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <span className="cinema-label">The Process</span>
            <h2 className="cinema-headline-lg mt-6 mb-4">
              The Creative <span className="text-cinema-gold">Journey</span>
            </h2>
            <p className="cinema-body-lg max-w-2xl mx-auto">
              From classroom to client-ready. See how we transform beginners into professionals.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {campusStories.map((story, index) => (
              <Reveal
                key={index}
                delay={index * 80}
                className="group relative overflow-hidden cinema-image-wrap"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img 
                    src={story.image}
                    alt={story.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-cinema-glow text-white px-3 py-1 text-xs font-medium">
                      {story.week}
                    </span>
                    <span className="text-cinema-gold text-xs uppercase tracking-[0.2em] font-sans">
                      {story.category}
                    </span>
                  </div>
                  <p className="font-display text-white text-lg leading-snug">
                    {story.caption}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-white/10">
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <Users className="w-7 h-7 text-cinema-gold" />
              </div>
              <div className="font-display text-3xl font-light text-cinema-gold mb-1">Max 15</div>
              <p className="text-xs uppercase tracking-[0.15em] text-cinema-muted font-sans">Students per Class</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <Award className="w-7 h-7 text-cinema-gold" />
              </div>
              <div className="font-display text-3xl font-light text-cinema-gold mb-1">95%</div>
              <p className="text-xs uppercase tracking-[0.15em] text-cinema-muted font-sans">Job Placement Rate</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <TrendingUp className="w-7 h-7 text-cinema-gold" />
              </div>
              <div className="font-display text-3xl font-light text-cinema-gold mb-1">3x</div>
              <p className="text-xs uppercase tracking-[0.15em] text-cinema-muted font-sans">Average Salary Increase</p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <CheckCircle className="w-7 h-7 text-cinema-gold" />
              </div>
              <div className="font-display text-3xl font-light text-cinema-gold mb-1">50+</div>
              <p className="text-xs uppercase tracking-[0.15em] text-cinema-muted font-sans">Successful Graduates</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE SUCCESS (Alumni Spotlight) — HIDDEN FOR NOW */}
      {false && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Reveal className="text-center mb-12">
            <span className="cinema-label">The Success</span>
            <h2 className="cinema-headline-lg mt-6 mb-4">
              Alumni <span className="text-cinema-gold">Success Stories</span>
            </h2>
            <p className="cinema-body-lg max-w-2xl mx-auto">
              Our graduates are working at top companies across Uganda and beyond.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {alumniSpotlight.map((alumni, index) => (
              <Reveal
                key={index}
                delay={index * 100}
                className="p-8 border-t border-white/5"
              >
                <div className="text-center mb-6">
                  <div className="inline-block relative mb-4">
                    <div className="w-24 h-24 bg-gradient-to-br from-cinema-gold to-cinema-glow p-1">
                      <div className="w-full h-full bg-cinema-graphite flex items-center justify-center text-2xl font-display font-light text-cinema-gold">
                        {alumni.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-cinema-glow p-2">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="font-display text-xl text-white mb-1">
                    {alumni.name}
                  </h3>
                  <p className="text-cinema-gold font-sans text-sm font-medium mb-1">
                    {alumni.role}
                  </p>
                  <p className="text-sm text-cinema-muted flex items-center justify-center gap-2 font-sans">
                    <Briefcase className="w-4 h-4" />
                    {alumni.company}
                  </p>
                </div>

                <div className="relative overflow-hidden mb-6 cinema-image-wrap">
                  <img
                    src={alumni.studentWork}
                    alt={`${alumni.name}'s work`}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute top-3 left-3 bg-cinema-glow text-white px-3 py-1 text-xs font-medium">
                    Student Project
                  </div>
                </div>

                <blockquote className="text-sm text-cinema-soft italic leading-relaxed mb-6 font-sans">
                  "{alumni.quote}"
                </blockquote>

                <div className="flex justify-center gap-3">
                  {alumni.linkedin && (
                    <a
                      href={alumni.linkedin}
                      className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {alumni.instagram && (
                    <a
                      href={alumni.instagram}
                      className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {alumni.portfolio && (
                    <a
                      href={alumni.portfolio}
                      className="p-2 text-cinema-muted hover:text-cinema-gold transition-colors"
                    >
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative bg-cinema-black border-t border-white/5 py-24 sm:py-32 overflow-hidden">
        <div className="cinema-glow-orb w-[500px] h-[500px] -top-32 left-1/2 -translate-x-1/2 opacity-50" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="cinema-headline-lg mb-6">
              Ready to Start Your <span className="text-cinema-gold">Creative Journey?</span>
            </h2>
            <p className="cinema-body-lg mb-10 max-w-2xl mx-auto">
              Join our next cohort or partner with us to hire talented creative professionals.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="cinema-btn-primary group">
                Apply for Next Cohort
                <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="mailto:synergymultimediaacademy@gmail.com?subject=Request Student Talent"
                className="cinema-btn-ghost"
              >
                <Briefcase className="w-5 h-5" />
                Request Student Talent
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
              title={selectedVideo.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Lightbox Modal - Full-Screen Minimalist */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <img 
              src={lightboxImage}
              alt="Full screen student portfolio work - Synergy Multimedia Academy Kampala showcase"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-cinema-black border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-cinema-muted text-sm font-sans">
            &copy; {new Date().getFullYear()} Synergy Concepts Hub. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
